import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CssBaseline, ThemeProvider } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from "react-router-dom";
import './helper/extensions';
import { showErrorMessage } from './helper/helper';
import { getLocalStorageToken, getToken } from './helper/TokenHelper';
import './i18n';
// Shared dialog/form primitives (ui-*). Loaded before index.scss so page styles build on top of them.
import '@pempem-org/front-styles/form-dialog';
import './index.scss';
import './pages/login/firebaseInit';
import { firebaseAuth } from './pages/login/firebaseInit';
import RoutesPempem from './pages/RoutesPempem';
import reportWebVitals from './reportWebVitals';
import { NFMStore } from './stores/store';
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ErrorResponse } from '@apollo/client/link/error';
import { PageTracking } from './components/PageTracking';
import { AppContextProvider, useAppContext } from './contexts/AppContext';

// import * as serviceWorkerRegistration from './serviceWorkerRegistration';

require('dayjs/locale/id')

// Needed to use utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)

const httpLink = new HttpLink({ uri: process.env.REACT_APP_BASE_URL });

const linkTokenHeader = setContext(async (_, { headers }) => {
  var accessToken
  if (headers?.token) {
    accessToken = headers.token
  } else {
    accessToken = await getAccessTokenPromise()
  }
  return {
    headers: {
      Authorization: "Bearer " + accessToken,
      "x-hasura-role": headers?.role ?? "user",
      "x-app-name": "NFM"
    }
  }
})

export function getAccessTokenPromise() {
  var token = getToken()
  const currentNumericDate = Math.round(Date.now() / 1000)

  if (token && currentNumericDate + 1 * 60 <= token.decode.exp) {
    return new Promise(resolve => resolve(token.token))
  } else {
    return new Promise(resolve => {
      if (firebaseAuth().currentUser) {
        var token = firebaseAuth().currentUser.getIdToken()
        resolve(token)
      } else if (getLocalStorageToken()) {
        resolve(getLocalStorageToken())
      } else {
        resolve(null)
      }
    })
  }
}

const errorHandler = (error: ErrorResponse) => {
  if (error.graphQLErrors) {
    error.graphQLErrors.forEach(({ message }) => {
      console.log(message);
      showErrorMessage(message);
    });
  }
  if (error.networkError) {
    console.log(error.networkError);
  }
};

const linkError = onError(errorHandler)

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache' as const,
    errorPolicy: 'ignore' as const,
  },
  query: {
    fetchPolicy: 'no-cache' as const,
    errorPolicy: 'all' as const,
  },
}

console.log('env', process.env.REACT_APP_ENV)

export const client = new ApolloClient({
  link: ApolloLink.from([linkError, linkTokenHeader, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: defaultOptions
});

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/** Reads the MUI theme from AppContext and wraps children in ThemeProvider + CssBaseline */
function ThemedApp({ children }: { children: React.ReactNode }) {
  const { muiTheme } = useAppContext();
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={NFMStore}>
      <BrowserRouter >
        <ApolloProvider client={client}>
          <PageTracking />
          <AppContextProvider>
            <ThemedApp>
              <RoutesPempem client={client} />
            </ThemedApp>
          </AppContextProvider>
        </ApolloProvider>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);


// serviceWorkerRegistration.register();


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();